import { createLeaderElection, TabLeaderElection } from '../utils/tab-leader-election';
import { TokenSyncBroadcastMessage } from '../types/session.types';
import { AuthTokens } from '../types/auth.types';

const FOLLOWER_TIMEOUT = 3000; // 3с — timeout ожидания результата от лидера
const MUTEX_KEY = 'token_refresh_mutex';
const MUTEX_STALE_MS = 10000; // 10с — mutex считается устаревшим
const CHANNEL_NAME = 'token_sync';

type TokenUpdateCallback = (accessToken: string, expiresIn: number) => void;
type LogoutCallback = () => void;

class TabSyncManager {
  private leader: TabLeaderElection | null = null;
  private channel: BroadcastChannel | null = null;
  private listeners: TokenUpdateCallback[] = [];
  private logoutListeners: LogoutCallback[] = [];
  private started = false;
  private storageHandler: ((e: StorageEvent) => void) | null = null;

  /**
   * Инициализация BroadcastChannel + leader election
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.leader = createLeaderElection('token_refresh');
    this.leader.start();

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<TokenSyncBroadcastMessage>) => {
        this.handleBroadcast(event.data);
      };
    } catch {
      // BroadcastChannel не поддерживается — fallback через StorageEvent
      console.warn('[TabSyncManager] BroadcastChannel не поддерживается, используем StorageEvent fallback');
    }

    // Fallback: слушаем StorageEvent для старых браузеров
    this.storageHandler = (e: StorageEvent) => {
      if (e.key === 'auth_tokens') {
        if (e.newValue) {
          try {
            const tokens: AuthTokens = JSON.parse(e.newValue);
            if (tokens.access_token) {
              this.notifyListeners(tokens.access_token, tokens.expires_in);
            }
          } catch { /* ignore parse errors */ }
        } else if (e.oldValue && !e.newValue) {
          // Токены удалены — logout из другой вкладки
          this.notifyLogoutListeners();
        }
      }
    };
    window.addEventListener('storage', this.storageHandler);
  }

  /**
   * Координированный refresh: лидер выполняет, follower-ы ждут
   */
  async coordinatedRefresh(performRefresh: () => Promise<AuthTokens>): Promise<AuthTokens> {
    // Любая вкладка (лидер или follower) пытается захватить mutex.
    // Если mutex свободен — выполняем refresh сами (не ждём несуществующего лидера).
    // Если mutex занят — ждём результат от того, кто его захватил.
    if (this.tryAcquireMutex()) {
      return this.leaderRefresh(performRefresh);
    }
    return this.followerRefresh(performRefresh);
  }

  /**
   * Уведомить другие вкладки об обновлении токена (после saveTokens)
   */
  notifyTokenUpdate(accessToken: string, expiresIn: number): void {
    this.broadcast({
      type: 'token-updated',
      accessToken,
      expiresIn,
      tabId: this.getTabId(),
    });
  }

  /**
   * Подписка на обновления токена от других вкладок
   */
  onTokenUpdate(cb: TokenUpdateCallback): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter(l => l !== cb);
    };
  }

  /**
   * Оповестить все вкладки о logout
   */
  broadcastLogout(): void {
    this.broadcast({
      type: 'token-logout',
      tabId: this.getTabId(),
    });
  }

  /**
   * Подписка на logout из другой вкладки
   */
  onLogout(cb: LogoutCallback): () => void {
    this.logoutListeners.push(cb);
    return () => {
      this.logoutListeners = this.logoutListeners.filter(l => l !== cb);
    };
  }

  /**
   * Является ли текущая вкладка лидером для scheduled refresh
   */
  isRefreshLeader(): boolean {
    return !this.leader || this.leader.isCurrentLeader();
  }

  /**
   * Очистка ресурсов
   */
  destroy(): void {
    if (!this.started) return;
    this.started = false;

    this.leader?.stop();
    this.leader = null;

    if (this.channel) {
      try { this.channel.close(); } catch { /* ignore */ }
      this.channel = null;
    }

    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler);
      this.storageHandler = null;
    }

    this.listeners = [];
    this.logoutListeners = [];
  }

  // --- Private ---

  private getTabId(): string {
    return this.leader?.getTabId() ?? 'unknown';
  }

  private async leaderRefresh(performRefresh: () => Promise<AuthTokens>): Promise<AuthTokens> {
    this.broadcast({
      type: 'token-refresh-start',
      tabId: this.getTabId(),
    });

    // mutex уже захвачен в coordinatedRefresh

    try {
      const tokens = await performRefresh();

      this.broadcast({
        type: 'token-refresh-success',
        accessToken: tokens.access_token,
        expiresIn: tokens.expires_in,
        tabId: this.getTabId(),
      });

      return tokens;
    } catch (error) {
      this.broadcast({
        type: 'token-refresh-failed',
        error: error instanceof Error ? error.message : 'Unknown error',
        tabId: this.getTabId(),
      });
      throw error;
    } finally {
      this.releaseMutex();
    }
  }

  private followerRefresh(performRefresh: () => Promise<AuthTokens>): Promise<AuthTokens> {
    return new Promise<AuthTokens>((resolve, reject) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout>;
      let unsubChannel: (() => void) | null = null;

      const settle = () => {
        settled = true;
        clearTimeout(timeoutId);
        unsubChannel?.();
      };

      // Слушаем BroadcastChannel
      if (this.channel) {
        const handler = (event: MessageEvent<TokenSyncBroadcastMessage>) => {
          if (settled) return;
          const msg = event.data;

          if (msg.type === 'token-refresh-success') {
            settle();
            resolve({
              access_token: msg.accessToken,
              refresh_token: '',
              token_type: 'bearer',
              expires_in: msg.expiresIn,
            });
          } else if (msg.type === 'token-refresh-failed') {
            settle();
            reject(new Error(`Leader refresh failed: ${msg.error}`));
          }
        };

        this.channel.addEventListener('message', handler);
        unsubChannel = () => this.channel?.removeEventListener('message', handler);
      }

      // Timeout → fallback к самостоятельному refresh
      timeoutId = setTimeout(async () => {
        if (settled) return;
        settle();
        console.warn('[TabSyncManager] Follower timeout, выполняем самостоятельный refresh');
        try {
          const tokens = await performRefresh();
          resolve(tokens);
        } catch (err) {
          reject(err);
        }
      }, FOLLOWER_TIMEOUT);
    });
  }

  private broadcast(msg: TokenSyncBroadcastMessage): void {
    if (this.channel) {
      try {
        this.channel.postMessage(msg);
      } catch {
        // ignore broadcast errors
      }
    }
  }

  private handleBroadcast(msg: TokenSyncBroadcastMessage): void {
    if (msg.tabId === this.getTabId()) return; // Игнорируем свои сообщения

    if (msg.type === 'token-updated') {
      this.notifyListeners(msg.accessToken, msg.expiresIn);
    } else if (msg.type === 'token-logout') {
      this.notifyLogoutListeners();
    }
  }

  private notifyListeners(accessToken: string, expiresIn: number): void {
    for (const cb of this.listeners) {
      try {
        cb(accessToken, expiresIn);
      } catch (error) {
        console.error('[TabSyncManager] Ошибка в token update callback:', error);
      }
    }
  }

  private notifyLogoutListeners(): void {
    for (const cb of this.logoutListeners) {
      try {
        cb();
      } catch (error) {
        console.error('[TabSyncManager] Ошибка в logout callback:', error);
      }
    }
  }

  private tryAcquireMutex(): boolean {
    try {
      const existing = localStorage.getItem(MUTEX_KEY);
      if (existing) {
        const parsed = JSON.parse(existing);
        // Если mutex принадлежит другой вкладке и не устарел — не захватываем
        if (parsed.tabId !== this.getTabId() && Date.now() - parsed.timestamp < MUTEX_STALE_MS) {
          return false;
        }
      }
      localStorage.setItem(MUTEX_KEY, JSON.stringify({
        tabId: this.getTabId(),
        timestamp: Date.now(),
      }));
      return true;
    } catch {
      // localStorage недоступен — разрешаем продолжить
      return true;
    }
  }

  private releaseMutex(): void {
    try {
      const data = localStorage.getItem(MUTEX_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        if (parsed.tabId === this.getTabId()) {
          localStorage.removeItem(MUTEX_KEY);
        }
      }
    } catch { /* ignore */ }
  }
}

const tabSyncManager = new TabSyncManager();
export default tabSyncManager;
